/**
 * Third-party brand marks, vendored from https://svgl.app/library/<name>.svg, except where a mark's
 * own file names another source (Google Meet's 2026 mark, Linear's app tile).
 *
 * Not part of the Lucide set. An icon is 24-unit inner geometry that takes stroke and colour from
 * the element around it. A logo is filled, owns its colours, and arrives on whatever grid its owner
 * drew it on (54x80 for Figma, 2447 for Slack), so each entry carries its own viewBox.
 *
 * Three marks are monochrome and are remapped once here so one drawing serves both themes:
 * GitHub's `#1b1f23` and Cursor's `#26251e` become `currentColor`, and Notion's white page
 * becomes `--background` with its glyph `--foreground`. Those are the only edits in
 * the set. Regenerate from svgl rather than restyling a mark to fit a surface.
 *
 * Every `id` is prefixed with the mark's key: inline SVGs share one document, so two marks that
 * both define `#a` would paint each other's gradients. Illustrator `<style>` class fills are
 * inlined as `fill` attributes for the same reason.
 *
 * OpenAI and Gemini come from Simple Icons (CC0), the same drawings as the core-ux prototype.
 *
 * One file per mark, named by its key. Add a mark by adding its file and its line below.
 */

import { arc } from './arc'
import { asana } from './asana'
import { ashby } from './ashby'
import { atlassian } from './atlassian'
import { brave } from './brave'
import { chrome } from './chrome'
import { claude } from './claude'
import { cursor } from './cursor'
import { discord } from './discord'
import { edge } from './edge'
import { figma } from './figma'
import { firefox } from './firefox'
import { gemini } from './gemini'
import { github } from './github'
import { gmail } from './gmail'
import { google } from './google'
import { googleAnalytics } from './google-analytics'
import { googleCalendar } from './google-calendar'
import { googleDocs } from './google-docs'
import { googleMeet } from './google-meet'
import { googleWorkspace } from './google-workspace'
import { granola } from './granola'
import { greenhouse } from './greenhouse'
import { linear } from './linear'
import { linearDark } from './linear-dark'
import { meta } from './meta'
import { microsoft } from './microsoft'
import { microsoftTeams } from './microsoft-teams'
import { notion } from './notion'
import { openai } from './openai'
import { outlook } from './outlook'
import { posthog } from './posthog'
import { safari } from './safari'
import { salesforce } from './salesforce'
import { slack } from './slack'

export type { LogoMark } from './types'

export const LOGOS = {
  arc,
  asana,
  ashby,
  atlassian,
  brave,
  chrome,
  claude,
  cursor,
  discord,
  edge,
  figma,
  firefox,
  gemini,
  github,
  gmail,
  google,
  'google-analytics': googleAnalytics,
  'google-calendar': googleCalendar,
  'google-docs': googleDocs,
  'google-meet': googleMeet,
  'google-workspace': googleWorkspace,
  granola,
  greenhouse,
  linear,
  'linear-dark': linearDark,
  meta,
  microsoft,
  'microsoft-teams': microsoftTeams,
  notion,
  openai,
  outlook,
  posthog,
  safari,
  salesforce,
  slack,
} as const

export type LogoName = keyof typeof LOGOS
